import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { url } from '../../util/config';

const HeaderComponent = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [userBalance, setUserBalance] = useState(0);
    const [allowedPaths, setAllowedPaths] = useState([]);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Read balance saved at login
        const bal = localStorage.getItem('balance');
        if (bal !== null) setUserBalance(Number(bal));

        // Read role-based allowed paths for settings dropdown visibility
        try {
            const roleString = localStorage.getItem('role');
            const roles = JSON.parse(roleString) || [];
            const paths = [];
            roles.forEach(item => {
                if (item.path) paths.push(item.path);
                if (item.subModules) item.subModules.forEach(sub => { if (sub.path) paths.push(sub.path); });
            });
            setAllowedPaths(paths);
        } catch { setAllowedPaths([]); }
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/users/logout`, {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (response.ok) {
                localStorage.removeItem('token');
                localStorage.removeItem('role');
                localStorage.removeItem('uid');
                localStorage.removeItem('balance');
                navigate('/login');
            } else {
                console.error('Logout failed:', response.statusText);
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const settingsLinks = [
        { path: '/manageuser', icon: 'bi-people', label: 'Manage Users' },
        { path: '/manageuserrole', icon: 'bi-shield-lock', label: 'Manage Roles' },
        { path: '/changepassword', icon: 'bi-key', label: 'Change Password' },
    ];

    // Only show settings links the user has access to
    const visibleLinks = settingsLinks.filter(link => allowedPaths.includes(link.path));

    return (
        <header style={styles.header}>
            {/* Left — Logo + App name */}
            <div style={styles.left}>
                <img src="/logo.jpeg" alt="Social CRM" style={styles.logo} />
                <span style={styles.appName}>Social CRM</span>
            </div>

            {/* Right — Balance + Settings */}
            <div style={styles.right}>
                {/* Balance chip */}
                <div style={styles.balanceChip}>
                    <i className="bi bi-wallet2" style={{ marginRight: 6 }}></i>
                    Balance: <strong style={{ marginLeft: 4 }}>₹{Number(userBalance).toLocaleString()}</strong>
                </div>

                {/* Settings dropdown */}
                <div style={styles.menuWrapper} ref={menuRef}>
                    <button style={styles.settingsBtn} onClick={() => setIsMenuOpen(!isMenuOpen)}>
                        <i className="bi bi-gear" style={{ marginRight: 6 }}></i>
                        Settings
                        <i className={`bi bi-chevron-${isMenuOpen ? 'up' : 'down'}`} style={{ marginLeft: 6, fontSize: 11 }}></i>
                    </button>

                    {isMenuOpen && (
                        <div style={styles.dropdown}>
                            {visibleLinks.map(link => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    style={{ ...styles.dropdownItem, ...(location.pathname === link.path ? styles.dropdownItemActive : {}) }}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <i className={`bi ${link.icon}`} style={{ marginRight: 10 }}></i>
                                    {link.label}
                                </Link>
                            ))}
                            {visibleLinks.length > 0 && <div style={styles.dropdownDivider} />}
                            <button style={styles.logoutBtn} onClick={handleLogout}>
                                <i className="bi bi-box-arrow-right" style={{ marginRight: 10 }}></i>
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

const styles = {
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', height: '60px', background: 'linear-gradient(to right, #1565c0, #2388e0)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', flexShrink: 0, zIndex: 100 },
    left: { display: 'flex', alignItems: 'center', gap: '12px' },
    logo: { height: '38px', width: '38px', borderRadius: '8px', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)' },
    appName: { color: 'white', fontWeight: '700', fontSize: '18px', letterSpacing: '-0.3px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    right: { display: 'flex', alignItems: 'center', gap: '16px' },
    balanceChip: { display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.15)', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', border: '1px solid rgba(255,255,255,0.25)' },
    menuWrapper: { position: 'relative' },
    settingsBtn: { display: 'flex', alignItems: 'center', background: 'white', color: '#1565c0', border: 'none', borderRadius: '6px', padding: '7px 14px', fontWeight: '600', fontSize: '13px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' },
    dropdown: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: 'white', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '200px', zIndex: 1050, padding: '6px', overflow: 'hidden' },
    dropdownItem: { display: 'flex', alignItems: 'center', padding: '9px 12px', borderRadius: '6px', fontSize: '14px', color: '#374151', textDecoration: 'none', fontWeight: '500', transition: 'background 0.15s' },
    dropdownItemActive: { background: '#eff6ff', color: '#1565c0' },
    dropdownDivider: { height: '1px', background: '#f3f4f6', margin: '4px 0' },
    logoutBtn: { display: 'flex', alignItems: 'center', width: '100%', padding: '9px 12px', borderRadius: '6px', fontSize: '14px', color: '#dc2626', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' },
};

export default HeaderComponent;
